import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Visibility, Delete, Add, Description } from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  CircularProgress
} from "@mui/material";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import { MainRoutes } from "common/config/route.config";
import useProfile from "common/hooks/useProfile";
import ConfirmationModal from "common/components/custom/confirmation-modal/ConfirmationModal";
import {
  ClassificationService,
  ClassificationListItem
} from "../../data/services/classification.service";
import "./DocumentHistoryPage.scss";

const DocumentHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { displayName } = useProfile();
  const [filterBy, setFilterBy] = useState<string>("all");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load classification data from API
  const [classificationList, setClassificationList] = useState<
    ClassificationListItem[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load documents on component mount
  useEffect(() => {
    const fetchClassificationList = async () => {
      try {
        setLoading(true);
        const response = await ClassificationService.getClassificationList();
        if (!response.hasErrors && response.value) {
          setClassificationList(response.value);
        } else {
          setError(
            response.errors?.join(", ") || "Failed to fetch classifications"
          );
        }
      } catch (err: any) {
        console.error("Error fetching classification list:", err);
        setError(err.message || "Failed to fetch classifications");
      } finally {
        setLoading(false);
      }
    };

    fetchClassificationList();
  }, []);

  const handleNewConfiguration = () => {
    navigate(MainRoutes.DATA_CLASSIFICATION_HUB);
  };

  const handleViewDocument = (document: ClassificationListItem) => {
    // Navigate to classification results page with batchGuid in URL
    navigate(
      MainRoutes.DATA_CLASSIFICATION_RESULTS.replace(
        ":batchGuid",
        document.batchGuid
      ),
      {
        state: {
          totalFiles: document.documentCount,
          classificationName: document.classificationName
        }
      }
    );
  };

  const handleEditDocument = (document: ClassificationListItem) => {
    // Edit
  };

  const handleDeleteDocument = (documentId: string) => {
    setDocumentToDelete(documentId);
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    if (documentToDelete) {
      setIsDeleting(true);
      try {
        const response = await ClassificationService.deleteClassificationBatch(
          documentToDelete
        );

        if (!response.hasErrors) {
          setClassificationList((prev) =>
            prev.filter((item) => item.batchGuid !== documentToDelete)
          );
        } else {
          console.error("Delete failed:", response.errors);
          setError(
            response.errors?.join(", ") || "Failed to delete classification"
          );
        }
      } catch (err: any) {
        console.error("Error deleting classification:", err);
        setError(err.message || "Failed to delete classification");
      } finally {
        setIsDeleting(false);
      }
    }
    setShowDeleteConfirmation(false);
    setDocumentToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
    setDocumentToDelete(null);
  };

  const getStatusIcon = (isProceeded: boolean) => {
    return isProceeded ? "✓" : "⟳";
  };

  const getStatusText = (isProceeded: boolean) => {
    return isProceeded ? "Completed" : "Under processed";
  };

  const getStatusColor = (isProceeded: boolean): "success" | "warning" => {
    return isProceeded ? "success" : "warning";
  };

  const filteredDocuments = classificationList.filter((doc) => {
    if (filterBy === "all") return true;
    // You can add more filtering logic here based on your needs
    return true;
  });

  if (loading) {
    return (
      <>
        <HeaderNew showSearch={false} />
        <div className="document-history-page">
          <HubHeader
            title="Classification Hub"
            description="Classify documents and validate category"
          />
          <div className="document-history-content">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "400px"
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="h6" style={{ marginLeft: "16px" }}>
                Loading classifications...
              </Typography>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <HeaderNew showSearch={false} />
        <div className="document-history-page">
          <HubHeader
            title="Classification Hub"
            description="Classify documents and validate category"
          />
          <div className="document-history-content">
            <div className="error-state">
              <Typography variant="h6" color="error">
                Error loading classifications
              </Typography>
              <Typography variant="body2">{error}</Typography>
              <Button
                variant="contained"
                onClick={() => window.location.reload()}
                style={{ marginTop: "16px" }}
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="document-history-page">
        <HubHeader
          title="Classification Hub"
          description="Classify documents and validate category"
        />

        <div className="document-history-content">
          <div className="page-header">
            <div className="header-left">
              <h2 className="page-title">Available Classifications</h2>
            </div>
            <div className="header-actions">
              {/* <Button
                variant="contained"
                startIcon={<Visibility />}
                onClick={() => navigate(MainRoutes.DATA_CLASSIFICATION_RESULTS)}
                className="new-config-button"
              >
                Classified Documents
              </Button> */}
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleNewConfiguration}
                className="new-config-button"
              >
                New Classification
              </Button>
            </div>
          </div>

          <div className="documents-grid">
            {filteredDocuments.map((document) => (
              <Card
                key={document.batchGuid}
                className="document-card"
                onClick={() => handleViewDocument(document)}
                sx={{ cursor: "pointer" }}
              >
                <CardContent className="card-content">
                  <div className="card-header">
                    <div className="document-icon">
                      <Description />
                    </div>
                    <h3 className="document-title">
                      {document.classificationName}
                    </h3>
                  </div>

                  <div className="document-details">
                    <div className="detail-row">
                      <div className="detail-item">
                        <div className="detail-content">
                          <Typography
                            variant="caption"
                            className="detail-label"
                          >
                            Company:
                          </Typography>
                          <Typography variant="body2" className="detail-value">
                            EY
                          </Typography>
                        </div>
                      </div>
                      <div className="detail-item">
                        <div className="detail-content">
                          <Typography
                            variant="caption"
                            className="detail-label"
                          >
                            Department:
                          </Typography>
                          <Typography variant="body2" className="detail-value">
                            Technology
                          </Typography>
                        </div>
                      </div>
                    </div>

                    <div className="detail-row">
                      <div className="detail-item">
                        <div className="detail-content">
                          <Typography
                            variant="caption"
                            className="detail-label"
                          >
                            Created by:
                          </Typography>
                          <Typography variant="body2" className="detail-value">
                            {displayName}
                            {/* {document.createdBy} */}
                          </Typography>
                        </div>
                      </div>
                      <div className="detail-item">
                        <div className="detail-content">
                          <Typography
                            variant="caption"
                            className="detail-label"
                          >
                            Created on:
                          </Typography>
                          <Typography variant="body2" className="detail-value">
                            {new Date(
                              document.createdDate
                            ).toLocaleDateString()}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="document-stats">
                    <div className="stats-item">
                      <Typography variant="body2" className="stats-label">
                        Documents Uploaded:
                      </Typography>
                      <Typography variant="h6" className="stats-value">
                        {document.documentCount}
                      </Typography>
                    </div>
                    {/* <Chip
                      size="small"
                      label={`${getStatusIcon(
                        document.isProceeded
                      )} ${getStatusText(document.isProceeded)}`}
                      color={getStatusColor(document.isProceeded)}
                      className="status-chip"
                    /> */}
                  </div>

                  {/* <div className="categories-section">
                    <Typography variant="caption" className="categories-label">
                      Categories:
                    </Typography>
                    <div className="categories-list">
                      {document.categories.map((category, index) => (
                        <Chip
                          key={index}
                          size="small"
                          label={category}
                          variant="outlined"
                          className="category-chip"
                        />
                      ))}
                    </div>
                  </div> */}

                  <div className="card-actions">
                    {/* <IconButton
                      size="small"
                      onClick={() => handleViewDocument(document)}
                      className="action-button view-button"
                      title="View"
                    >
                      <Visibility />
                    </IconButton> */}
                    {/* <IconButton
                      size="small"
                      onClick={() => handleEditDocument(document)}
                      className="action-button edit-button"
                      title="Edit"
                    >
                      <Edit />
                    </IconButton> */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDocument(document.batchGuid);
                      }}
                      className="action-button delete-button"
                      title="Delete Classification"
                    >
                      <Delete />
                    </IconButton>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredDocuments.length === 0 && (
            <div className="empty-state">
              <Description className="empty-icon" />
              <Typography variant="h6" className="empty-title">
                No classifications found
              </Typography>
              <Typography variant="body2" className="empty-description">
                No documents available for classification.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleNewConfiguration}
                className="empty-action-button"
              >
                Upload Your First Documents
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        title="Delete Document Classification"
        message={`Are you sure you want to delete "${
          filteredDocuments.find((doc) => doc.batchGuid === documentToDelete)
            ?.classificationName
        }"? This action cannot be undone.`}
        variant="danger"
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isLoading={isDeleting}
      />
    </>
  );
};

export default DocumentHistoryPage;
